import { useQuery } from '@tanstack/vue-query'
import { computed, toValue, type MaybeRefOrGetter } from 'vue'

import { getOfferingVaspListingReadiness } from '../ordersEndpoints'
import { ordersQueryKeys } from '../ordersQueryKeys'

/**
 * How many of an offering's orders are ready for token issuance (LST-04).
 *
 * Published for the offerings module's VAISP onboarding drawer
 * (architecture-principles §10 — shared-endpoint hooks). It lives here because
 * the endpoint hangs off the orders controller and the figure is a rollup of
 * order state; the offering drawer is only where an operator needs to read it,
 * next to the `Register trade with DLD` action whose refusal it explains.
 *
 * **Reported, not enforced here.** Since LST-04 admin-service runs the same check
 * and refuses the registration itself (409
 * VASP_LISTING_ORDERS_NOT_READY_FOR_REGISTRATION), so the server is the gate. This
 * query drives what the confirm dialog says and whether confirming it counts as an
 * acknowledgement; `isReady` still never reaches a `disabled` binding, because this
 * rollup can be a refetch behind the truth and blocking a call the server would
 * have accepted is worse than letting the server answer.
 *
 * Gated on a truthy id so nothing is fetched until a row is selected.
 */
export function useOfferingVaspListingReadinessQuery(
  offeringId: MaybeRefOrGetter<string | null | undefined>,
) {
  const idRef = computed(() => String(toValue(offeringId) ?? ''))
  return useQuery({
    queryKey: computed(() => ordersQueryKeys.vaspListingReadiness(idRef.value)),
    queryFn: () => getOfferingVaspListingReadiness(idRef.value),
    enabled: computed(() => Boolean(idRef.value)),
  })
}
